import React, { useState } from "react";
import { RefreshCw } from "lucide-react";

import WeightChart from "./shared/WeightChart";
import PeriodSelector, { InsightsPeriod } from "./shared/PeriodSelector";
import { getWeekStart, getWeekEnd } from "./shared/WeekPicker";

import WeeklyInsightsView from "./Weekly/WeeklyInsightsView";
import WeeklyInsightsHeader from "./Weekly/WeeklyInsightsHeader";

import FortnightlyInsightsView from "./Fortnightly/FortnightlyInsightsView";
import FortnightlyInsightsHeader, {
  getFortnightStart,
} from "./Fortnightly/FortnightlyInsightsHeader";

import MonthlyInsightsView from "./Monthly/MonthlyInsightsView";
import MonthlyInsightsHeader, { getMonthStart } from "./Monthly/MonthlyInsightsHeader";

interface InsightsTabProps {
  userId: number;
  targetWeight?: number;
}

const InsightsTab: React.FC<InsightsTabProps> = ({ userId, targetWeight }) => {
  const [period, setPeriod] = useState<InsightsPeriod>("weekly");

  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(new Date()));
  const [weekEnd, setWeekEnd] = useState<Date>(() => getWeekEnd(new Date()));

  const [monthStart, setMonthStart] = useState<Date>(() => getMonthStart(new Date()));

  const [fortnightStart, setFortnightStart] = useState<Date>(() => {
    const ws = getFortnightStart(new Date());
    ws.setDate(ws.getDate() - 7);
    return ws;
  });

  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);

  return (
    <div className="flex flex-col py-4">
      <WeightChart userId={userId} targetWeight={targetWeight} />

      {/* Period selector + inline date picker */}
      <div className="flex items-center justify-between px-6 mb-3">
        <div className="flex items-center gap-2">
          <PeriodSelector period={period} onChange={setPeriod} />
          <button
            onClick={() => setRefreshKey((k) => k + 1)}
            disabled={isLoadingInsights}
            className="p-1.5 rounded transition hover:bg-gray-100 disabled:cursor-not-allowed"
            aria-label="Refresh insights"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 text-[#00A9A9] transition-transform ${
                isLoadingInsights ? "animate-spin" : ""
              }`}
            />
          </button>
        </div>

        {period === "weekly" && (
          <WeeklyInsightsHeader
            onWeekChange={(ws, we) => {
              setWeekStart(ws);
              setWeekEnd(we);
            }}
          />
        )}
        {period === "fortnightly" && (
          <FortnightlyInsightsHeader onFortnightChange={(ws) => setFortnightStart(ws)} />
        )}
        {period === "monthly" && (
          <MonthlyInsightsHeader onMonthChange={(ms) => setMonthStart(ms)} />
        )}
      </div>

      {period === "weekly" && (
        <WeeklyInsightsView
          userId={userId}
          weekStart={weekStart}
          weekEnd={weekEnd}
          refreshKey={refreshKey}
          onLoadingChange={setIsLoadingInsights}
        />
      )}

      {period === "fortnightly" && (
        <FortnightlyInsightsView
          userId={userId}
          week1Start={fortnightStart}
          refreshKey={refreshKey}
          onLoadingChange={setIsLoadingInsights}
        />
      )}

      {period === "monthly" && (
        <MonthlyInsightsView
          userId={userId}
          monthStart={monthStart}
          refreshKey={refreshKey}
          onLoadingChange={setIsLoadingInsights}
        />
      )}
    </div>
  );
};

export default InsightsTab;
